import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { ArrowLeft, Loader2 } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  confirmed: "bg-green-500/20 text-green-300 border-green-500/40",
  cancelled: "bg-red-500/20 text-red-300 border-red-500/40",
};

export default function Dashboard() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    api.get("/bookings/me").then(r => setBookings(r.data)).catch(() => {}).finally(() => setBusy(false));
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-16" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-bold text-zinc-500 hover:text-scooter-yellow mb-6" data-testid="back-home-btn">
          <ArrowLeft className="w-3 h-3" /> Home
        </button>
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <h1 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter">{t.dash.title}</h1>
          {user && <div className="text-sm text-zinc-400">{user.email}</div>}
        </div>
        {busy ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-scooter-yellow" /></div>
        ) : bookings.length === 0 ? (
          <div className="border border-white/10 p-12 text-center text-zinc-400" data-testid="empty-state">{t.dash.empty}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bookings-cards">
            {bookings.map(b => {
              const statusMap = { pending: { label: "In attesa pagamento", color: "text-yellow-300 border-yellow-500/40 bg-yellow-500/5" }, confirmed: { label: "Confermata ✓", color: "text-green-300 border-green-500/40 bg-green-500/5" }, cancelled: { label: "Annullata", color: "text-red-300 border-red-500/40 bg-red-500/5" } };
              const st = statusMap[b.status] || { label: b.status, color: "text-zinc-300 border-white/20" };
              const helmets = b.helmets ? Object.entries(b.helmets).filter(([, v]) => v > 0).map(([k, v]) => `${v}×${k}`).join(" · ") : "";
              return (
                <div key={b.booking_id} className={`border ${st.color} p-5 flex flex-col gap-3`} data-testid={`booking-row-${b.booking_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-zinc-500">Prenotazione</div>
                      <div className="font-mono text-sm text-scooter-yellow font-bold">{b.booking_id}</div>
                    </div>
                    <div className={`text-[9px] tracking-[0.25em] uppercase font-bold px-2 py-1 border ${st.color}`}>{st.label}</div>
                  </div>
                  <div className="flex items-center gap-4 pt-2 border-t border-white/10">
                    <img src="https://customer-assets.emergentagent.com/job_scooter-rental-book/artifacts/kidb633j_image.png" alt="NIU NQiX 500" className="h-16 w-16 object-contain flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-zinc-400 uppercase tracking-wider">NIU NQiX 500</div>
                      <div className="text-white text-sm font-bold" data-testid={`dash-units-${b.booking_id}`}>{(b.unit_names?.length > 0) ? b.unit_names.join(" · ") : `${b.quantity}× scooter`}</div>
                      <div className="text-[11px] text-zinc-500 mt-1">{b.pricing_tier === "half_day" ? "Pomeridiano" : b.pricing_tier === "multi_day" ? "Multi-giorno" : "Giornata intera"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-zinc-500">Totale</div>
                      <div className="font-heading font-black text-2xl text-scooter-yellow leading-none">€{b.total_price.toFixed(0)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                    <div>
                      <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-zinc-500">Ritiro</div>
                      <div className="text-white font-bold">{b.start_date}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-zinc-500">Riconsegna</div>
                      <div className="text-white font-bold">{b.end_date}</div>
                    </div>
                  </div>
                  {(helmets || (b.topcases || 0) > 0) && (
                    <div className="text-[11px] text-zinc-400 pt-2 border-t border-white/10">
                      {helmets && <>🪖 {helmets}</>}
                      {(b.topcases || 0) > 0 && <> · 📦 {b.topcases}× bauletto</>}
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-600">📍 Via Glira 4, San Cassiano · +39 335 6116528</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
